import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import styles from "./SSFCategoryBanner.module.css";

/**
 * View component. Displays an icon and text information about an SSF category.
 */
export function SSFCategoryBanner({
  iconSrc,
  pendingIconFile,
  title,
  subtitle,
  stepNumber,
  textColor,
  fillColor,
}) {
  const [previewUrl, setPreviewUrl] = useState(null);

  // generate preview URL if there is a file upload pending
  useEffect(() => {
    if (pendingIconFile) {
      const url = URL.createObjectURL(pendingIconFile);

      setPreviewUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setPreviewUrl(null);
    }
  }, [pendingIconFile]);

  return (
    <div
      className={styles.banner}
      role="group"
      aria-label={`${title}, Step ${stepNumber}`}
      style={{ "--ssf-background": fillColor, "--ssf-foreground": textColor }}
    >
      <div className={styles.iconFrame}>
        {(previewUrl || iconSrc) && (
          <img
            className={styles.icon}
            src={previewUrl || iconSrc}
            alt=""
            draggable={false}
          />
        )}
      </div>
      <div className={styles.copy}>
        <div className={styles.title}>{title.toUpperCase()}</div>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>
      <div className={styles.step} aria-label={`Step ${stepNumber}`}>
        <span className={styles.stepLabel}>Step</span>
        <span className={styles.stepCount}>{stepNumber}</span>
      </div>
    </div>
  );
}

SSFCategoryBanner.propTypes = {
  /**
   * Category icon's source URL
   */
  iconSrc: PropTypes.string,

  /**
   * Category title
   */
  title: PropTypes.string.isRequired,

  /**
   * Category subtitle
   */
  subtitle: PropTypes.string.isRequired,

  /**
   * Category step number
   */
  stepNumber: PropTypes.number.isRequired,

  /**
   * Banner text color as a CSS color string
   */
  textColor: PropTypes.string.isRequired,

  /**
   * Banner background color as a CSS color string
   */
  fillColor: PropTypes.string.isRequired,
};
